/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Hidden, Skeleton, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { Button as ButtonType } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import CollectionFilterChips from 'bundles/cds-labs/components/Modules/CollectionsModule/CollectionFilterChips';
import SpotlightCollectionsModule from 'bundles/cds-labs/components/Modules/CollectionsModule/SpotlightCollectionsModule';
// Internal type import for this component
import type { CollectionsModuleProps as Props } from 'bundles/cds-labs/components/Modules/CollectionsModule/types';
import { COLLECTIONS_MODULE_DISPLAY_STYLE } from 'bundles/cds-labs/components/Modules/CollectionsModule/types';
import ModuleLayout, {
  MODULE_LAYOUTS,
  getModuleLayoutConfig,
  isHorizontalLayout,
} from 'bundles/cds-labs/components/Modules/ModuleLayout';
import Eyebrow from 'bundles/cds-labs/components/Modules/components/Eyebrow';
import {
  TYPOGRAPHY_DARK_COLOR,
  TYPOGRAPHY_LIGHT_COLOR,
  parseTextColorOverride,
  resolveIsDarkBackground,
} from 'bundles/cds-labs/components/Modules/utils/colors';
import { getIsSideBySideLayout } from 'bundles/cds-labs/components/Modules/utils/constants';
import type { GridConfig } from 'bundles/cds-labs/components/ShowMoreGridSection/ShowMoreGridSection';
// External Shared Dependencies
import CollectionsContainer from 'bundles/collections-common/components/CollectionsContainer';

import _t from 'i18n!nls/cds-labs';

// Re-export shared types and constants for backward compatibility
export type {
  CTA,
  Filter,
  HeaderLevel,
  CollectionsModuleProps as Props,
} from 'bundles/cds-labs/components/Modules/CollectionsModule/types';
export {
  COLLECTIONS_MODULE_DISPLAY_STYLE,
  headerOrder,
} from 'bundles/cds-labs/components/Modules/CollectionsModule/types';

const styles = {
  eyebrowSpacing: css`
    margin-bottom: var(--cds-spacing-150);
  `,

  description: css`
    margin-top: var(--cds-spacing-50);
  `,

  alignCta: css`
    margin-top: var(--cds-spacing-150);
  `,

  collectionsWrapper: css`
    width: 100%;
    align-self: flex-start;
  `,

  collectionWrapperVertical: css`
    margin-top: calc(-1 * var(--cds-spacing-300));

    ${breakpoints.down('sm')} {
      margin-top: calc(-1 * var(--cds-spacing-200));
    }

    ${breakpoints.down('xs')} {
      margin-top: 0;
    }
  `,

  collectionWrapperHorizontal: css`
    // this is to override the min-width applied to CDS product cards
    // to prevent the product cards from overlapping with each other
    .cds-ProductCard-grid {
      min-width: unset !important;
    }
  `,

  collections: css`
    margin-top: calc(-1 * var(--cds-spacing-400));
    margin-bottom: calc(-1 * var(--cds-spacing-400));

    .cds-ProductCard-grid {
      min-width: unset;
    }
  `,

  collectionListContainer: css`
    && {
      ${breakpoints.up('sm')} {
        ul {
          margin: calc(var(--cds-spacing-100) * -1);
          width: calc(100% + var(--cds-spacing-200));
        }
      }
    }
  `,

  button: css`
    &:hover,
    &:active,
    &:visited {
      text-decoration: none;
    }
  `,

  ghostCta: css`
    &:hover,
    &:focus-visible {
      background-color: transparent;
      text-decoration: underline;
    }
  `,

  marginTop600: (breakpoint: 'md' | 'lg') => css`
    ${breakpoints.up(breakpoint)} {
      margin-top: var(--cds-spacing-600);
    }
  `,
  marginTop1000: (breakpoint: 'md' | 'lg') => css`
    ${breakpoints.up(breakpoint)} {
      margin-top: var(--cds-spacing-1000);
    }
  `,
  marginTop1400: (breakpoint: 'md' | 'lg') => css`
    ${breakpoints.up(breakpoint)} {
      margin-top: var(--cds-spacing-1400);
    }
  `,
  noResultsFound: css`
    margin-top: var(--cds-spacing-800);
    margin-bottom: var(--cds-spacing-400);
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;

    p {
      margin-top: var(--cds-spacing-100);
    }
  `,
  customContentPanelStyles: {
    default: (hasFilters: boolean) => css`
      && {
        ${hasFilters
          ? `padding-bottom: var(--cds-spacing-100);

        ${breakpoints.down('xs')} {
          padding-bottom: 0;
        }`
          : `padding-bottom: var(--cds-spacing-200);

          ${breakpoints.down('xs')} {
          padding-bottom: var(--cds-spacing-100);
        }
        `}
      }
    `,
    sideBySideLayout: css`
      && {
        padding-bottom: 0;
      }
    `,
  },

  customImagePanelStyles: {
    default: css`
      ${breakpoints.down('xs')} {
        && {
          padding-top: 0;
        }
      }
    `,
    sideBySideLayout: css`
      && {
        padding-top: var(--cds-spacing-150);
      }
    `,
  },
};

function CollectionsModule(props: Props) {
  const {
    filters,
    layout,
    showAllEntries,
    onFilterChange,
    backgroundColor,
    cta,
    description,
    headline,
    headlineComponent,
    collections = [],
    isCollectionsLoading,
    currentCollectionSetIndex = 0,
    numOfEntriesPerCollection,
    customCtaButton,
    customChipStyles,
    alwaysShowChipFilters,
    productCardTrackingName,
    eventingData,
    customHeadline,
    eyebrow,
    customItemsGridConfig,
    customBackgroundColor,
    textColorOverride,
    displayStyle,
    dataTestId,
  } = props;

  const layoutConfig = getModuleLayoutConfig(props);
  const isXsOrLess = useMediaQuery(breakpoints.down('xs'));
  const isMdOrLess = useMediaQuery(breakpoints.down('md'));
  const isSmOrLess = useMediaQuery(breakpoints.down('sm'));

  // Spotlight collections are rendered by a separate module to avoid CollectionsContainer dependencies
  // being pulled into bundles that only make use of spotlight-style collections (e.g. front-page).
  if (displayStyle === COLLECTIONS_MODULE_DISPLAY_STYLE.SPOTLIGHT) {
    return <SpotlightCollectionsModule {...props} />;
  }

  const isTwoByTenLayout =
    layout === MODULE_LAYOUTS.text2image10 ||
    layout === MODULE_LAYOUTS.image10text2 ||
    layout === MODULE_LAYOUTS.text10image2 ||
    layout === MODULE_LAYOUTS.image2text10;
  const isThreeByNineLayout = layout === MODULE_LAYOUTS.text3image9 || layout === MODULE_LAYOUTS.image9text3;
  const isSideBySideLayout = getIsSideBySideLayout(layout);

  // show one less entry if there is an odd number of entries, to prevent an orphaned card on the last row
  const numOfEntriesPerCollectionToDisplay =
    showAllEntries && numOfEntriesPerCollection && numOfEntriesPerCollection % 2 !== 0 && isSmOrLess
      ? numOfEntriesPerCollection - 1
      : numOfEntriesPerCollection;

  const isHorizontal = isHorizontalLayout({ layout });

  const isMobileListViewMdBreakpoint =
    !!numOfEntriesPerCollectionToDisplay && numOfEntriesPerCollectionToDisplay <= 2 && !isHorizontal && isMdOrLess;

  const itemsGridConfig: GridConfig =
    customItemsGridConfig ?? (isHorizontal ? { lg: 4, md: 4, sm: 6, xs: 12 } : { lg: 3, md: 3, sm: 6, xs: 12 });
  const collectionsToDisplay = numOfEntriesPerCollectionToDisplay
    ? collections.map((collection) => ({
        ...collection,
        entities: collection.entities.slice(0, numOfEntriesPerCollectionToDisplay),
      }))
    : collections;

  const parsedTextColorOverride = parseTextColorOverride(textColorOverride);
  const isInverted = resolveIsDarkBackground({
    backgroundColor,
    customBackgroundColor,
    textColorOverride: parsedTextColorOverride,
  });

  // Prefer customBackgroundColor over named backgroundColor for ModuleLayout
  const effectiveBackgroundColor = customBackgroundColor || layoutConfig.backgroundColor;

  // Override text color when textColorOverride disagrees with ModuleLayout's auto-detection
  const autoDetectedInverted = resolveIsDarkBackground({ backgroundColor: effectiveBackgroundColor });
  const textColorOverrideCss =
    isInverted !== autoDetectedInverted
      ? css`
          color: ${isInverted ? TYPOGRAPHY_LIGHT_COLOR : TYPOGRAPHY_DARK_COLOR};
        `
      : undefined;

  let variantOfCta: React.ComponentProps<typeof ButtonType>['variant'] = cta?.variant ?? 'secondary';
  if (cta?.variant === 'ghost') {
    variantOfCta = isInverted ? 'ghostInvert' : 'ghost';
  }

  let button: React.ReactNode;
  if (customCtaButton) {
    button = customCtaButton;
  } else {
    button =
      cta?.text && cta?.target ? (
        <div css={styles.alignCta}>
          <Button
            component="a"
            variant={variantOfCta}
            size={cta.size ?? undefined}
            icon={cta.showCtaArrowIcon || cta.variant === 'ghost' ? <ArrowNextIcon /> : undefined}
            href={cta.target}
            onClick={cta.onClick}
            css={cta.variant === 'ghost' ? styles.ghostCta : styles.button}
            fullWidth={isXsOrLess}
            edgeAlign={isXsOrLess ? undefined : 'start'}
          >
            {cta.text}
          </Button>
        </div>
      ) : null;
  }

  const shouldRenderImagesForFilters =
    filters?.reduce((acc, filter) => {
      return filter.filterImage ? acc + 1 : acc;
    }, 0) === filters?.length;
  const hasFilters = !!filters && (filters.length > 1 || !!alwaysShowChipFilters);

  const collectionCount = collectionsToDisplay.reduce((count, collection) => {
    return count + (collection.entities?.length ?? 0);
  }, 0);

  const getCustomPanelStyles = (panelType: 'content' | 'image') => {
    if (!isSideBySideLayout) {
      if (panelType === 'content') {
        return [styles.customContentPanelStyles.default(hasFilters)];
      } else {
        return [styles.customImagePanelStyles.default];
      }
    }

    const styleSet = panelType === 'content' ? styles.customContentPanelStyles : styles.customImagePanelStyles;
    if ((isTwoByTenLayout && isMdOrLess) || (isThreeByNineLayout && isSmOrLess)) {
      return [styleSet.sideBySideLayout];
    }

    return [];
  };

  const renderFilterChips = () => {
    if (!hasFilters || !filters) return null;
    return (
      <CollectionFilterChips
        filters={filters}
        currentCollectionSetIndex={currentCollectionSetIndex}
        isSideBySideLayout={isSideBySideLayout}
        shouldRenderImagesForFilters={!!shouldRenderImagesForFilters}
        customChipStyles={customChipStyles}
        onFilterChange={onFilterChange}
        collections={collections}
        eventingData={eventingData}
      />
    );
  };

  const renderImage = () => {
    return (
      <div
        css={[
          styles.collectionsWrapper,
          isHorizontal ? styles.collectionWrapperHorizontal : styles.collectionWrapperVertical,
        ]}
      >
        {renderFilterChips()}
        <div
          css={styles.collections}
          id="collections-results"
          role="tabpanel"
          data-e2e={isCollectionsLoading ? 'collections-loading' : 'collections-loaded'}
        >
          {!isCollectionsLoading && collectionCount === 0 && filters?.length ? (
            <div css={styles.noResultsFound}>
              <Typography2 variant="subtitleLarge" component="h3">
                {_t('No results found for "#{filterName}"', {
                  filterName: filters[currentCollectionSetIndex]?.filterName,
                })}
              </Typography2>
              <Typography2 variant="bodyPrimary" component="p">
                {_t('Try changing your filters')}
              </Typography2>
            </div>
          ) : (
            <div css={styles.collectionListContainer}>
              <CollectionsContainer
                eventingData={{
                  ...eventingData,
                  ...(filters?.[currentCollectionSetIndex]?.eventingData?.carouselFilter
                    ? { carouselFilter: filters?.[currentCollectionSetIndex]?.eventingData?.carouselFilter }
                    : {}),
                }}
                productCardTrackingName={productCardTrackingName}
                collections={collectionsToDisplay}
                isCollectionsLoading={isCollectionsLoading}
                itemsGridConfig={itemsGridConfig}
                hideLoadingTitle
                showAllEntries={showAllEntries ?? false}
                renderTitle={() => null}
                cardSpacing={{ xs: 8, sm: 16 }}
              />
            </div>
          )}
        </div>
        <Hidden {...(isTwoByTenLayout || isMobileListViewMdBreakpoint ? { lgUp: true } : { mdUp: true })}>
          {button}
        </Hidden>
      </div>
    );
  };

  const getMarginTopStyle = () => {
    const breakpoint = isTwoByTenLayout ? 'lg' : 'md';
    if (hasFilters && numOfEntriesPerCollectionToDisplay != null) {
      if (numOfEntriesPerCollectionToDisplay <= 2) {
        return styles.marginTop600(breakpoint);
      } else {
        return styles.marginTop1400(breakpoint);
      }
    }
    return styles.marginTop1000(breakpoint);
  };

  const renderContent = () => {
    if (isCollectionsLoading && !headline && !customHeadline) {
      return (
        <div>
          <Skeleton data-testid="skeleton" width="70%" height={40} />
        </div>
      );
    }

    return (
      <div css={isSideBySideLayout ? getMarginTopStyle() : null}>
        {eyebrow?.image && (
          <div css={styles.eyebrowSpacing}>
            <Eyebrow image={eyebrow.image} />
          </div>
        )}
        {customHeadline || (
          <Typography2
            variant={isSideBySideLayout ? 'titleXSmall' : 'subtitleLarge'}
            component={headlineComponent ?? 'h2'}
            color="inherit"
          >
            {headline}
          </Typography2>
        )}
        {description && (
          <Typography2 variant="bodyPrimary" component="div" color="inherit" css={styles.description}>
            {description}
          </Typography2>
        )}
        <Hidden {...(isTwoByTenLayout || isMobileListViewMdBreakpoint ? { mdDown: true } : { smDown: true })}>
          {button}
        </Hidden>
      </div>
    );
  };

  return (
    <ModuleLayout
      renderContent={renderContent}
      renderImage={renderImage}
      dataTestId={dataTestId}
      assembly={{
        ...layoutConfig,
        backgroundColor: effectiveBackgroundColor,
        layout,
        customContentPanelStyles: getCustomPanelStyles('content'),
        customImagePanelStyles: getCustomPanelStyles('image'),
        customPageContainerStyles: [
          ...(layoutConfig.customPageContainerStyles ?? []),
          ...(textColorOverrideCss ? [textColorOverrideCss] : []),
        ],
      }}
    />
  );
}

export default CollectionsModule;
